import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  getProviders,
  deleteProvider,
  deactivateProvider,
  type Provider,
  type CreateProviderResult,
} from "../api/client";
import { ProviderForm } from "../components/ProviderForm";

function fmtDate(iso: string | null) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("it-IT", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function providerStatus(p: Provider): { label: string; cls: string } {
  if (!p.active) return { label: "Inattivo", cls: "badge-neutral" };
  if (p.expires_at && new Date(p.expires_at) < new Date())
    return { label: "Scaduto", cls: "badge-danger" };
  return { label: "Attivo", cls: "badge-success" };
}

function ProviderCard({
  provider,
  onEdit,
  onDetail,
  onDeactivate,
  onDelete,
}: {
  provider: Provider;
  onEdit: () => void;
  onDetail: () => void;
  onDeactivate: () => void;
  onDelete: () => void;
}) {
  const status = providerStatus(provider);

  return (
    <div className="card">
      {/* Header row */}
      <div className="card-header">
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "var(--space-2)",
            flexWrap: "wrap",
          }}
        >
          <h3
            style={{
              margin: 0,
              fontSize: "var(--font-size-md)",
              fontWeight: "var(--font-weight-semibold)",
              color: "var(--indaco-brand-dark)",
            }}
          >
            {provider.name}
          </h3>
          <span className={`badge ${status.cls}`}>{status.label}</span>
        </div>
        <div style={{ display: "flex", gap: "var(--space-2)", flexWrap: "wrap" }}>
          <button className="btn btn-sm btn-ghost" onClick={onDetail}>
            <span className="material-symbols-outlined" style={{ fontSize: 16 }}>open_in_new</span>
            Dettaglio
          </button>
          <button className="btn btn-sm btn-outline" onClick={onEdit}>
            <span className="material-symbols-outlined" style={{ fontSize: 16 }}>edit</span>
            Modifica
          </button>
          {provider.active ? (
            <button className="btn btn-sm btn-outline" onClick={onDeactivate}
              style={{ color: "var(--indaco-warning)", borderColor: "var(--indaco-warning)" }}>
              <span className="material-symbols-outlined" style={{ fontSize: 16 }}>block</span>
              Disattiva
            </button>
          ) : (
            <button className="btn btn-sm btn-outline" onClick={onDeactivate}
              style={{ color: "var(--indaco-success)", borderColor: "var(--indaco-success)" }}>
              <span className="material-symbols-outlined" style={{ fontSize: 16 }}>check_circle</span>
              Riattiva
            </button>
          )}
          <button className="btn btn-sm btn-danger" onClick={onDelete}>
            <span className="material-symbols-outlined" style={{ fontSize: 16 }}>delete</span>
            Elimina
          </button>
        </div>
      </div>

      {provider.description && (
        <p
          style={{
            fontSize: "var(--font-size-sm)",
            color: "var(--indaco-gray-600)",
            margin: "var(--space-1) 0 var(--space-3)",
            lineHeight: 1.4,
          }}
        >
          {provider.description}
        </p>
      )}

      {/* Product + recognition type badges */}
      <div
        style={{
          display: "flex",
          gap: "var(--space-2)",
          flexWrap: "wrap",
          marginBottom: "var(--space-4)",
        }}
      >
        {provider.allowed_products.map((p) => (
          <span
            key={p}
            className="badge"
            style={{
              background:
                p.toLowerCase() === "top"
                  ? "var(--indaco-info-light)"
                  : "#F3E8FF",
              color:
                p.toLowerCase() === "top" ? "var(--indaco-info)" : "#7C3AED",
            }}
          >
            {p.toUpperCase()}
          </span>
        ))}
        {provider.allowed_recognition_types.map((rt) => (
          <span
            key={rt}
            className="badge badge-neutral"
            style={{ fontSize: "0.65rem" }}
          >
            {rt.replace("FEA_", "")}
          </span>
        ))}
      </div>

      {/* Stats grid */}
      <div
        className="detail-grid"
        style={{ gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))" }}
      >
        <div>
          <span className="detail-label">Sessioni</span>
          <span className="detail-value">{provider.session_count}</span>
        </div>
        <div>
          <span className="detail-label">Ultima attività</span>
          <span className="detail-value">{fmtDate(provider.last_session_at)}</span>
        </div>
        <div>
          <span className="detail-label">API key</span>
          <span
            className="detail-value"
            style={{
              fontFamily: "monospace",
              fontSize: "var(--font-size-xs)",
              color: "var(--indaco-gray-600)",
            }}
          >
            {provider.api_key_prefix}…
          </span>
        </div>
        <div>
          <span className="detail-label">Scadenza</span>
          <span className="detail-value">
            {provider.expires_at ? fmtDate(provider.expires_at) : "Nessuna scadenza"}
          </span>
        </div>
      </div>
    </div>
  );
}

type View = "list" | "create" | { edit: Provider };
type ConfirmAction = { type: "deactivate"; provider: Provider } | { type: "delete"; provider: Provider };

export function Providers() {
  const navigate = useNavigate();
  const [providers, setProviders] = useState<Provider[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [view, setView] = useState<View>("list");
  const [confirmAction, setConfirmAction] = useState<ConfirmAction | null>(null);
  const [acting, setActing] = useState(false);

  const loadProviders = () => {
    setLoading(true);
    getProviders()
      .then(setProviders)
      .catch((e) => setError((e as Error).message))
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadProviders(); }, []);

  const handleFormDone = (_created?: CreateProviderResult) => {
    setView("list");
    loadProviders();
  };

  const handleConfirm = () => {
    if (!confirmAction) return;
    setActing(true);
    const action = confirmAction.type === "deactivate"
      ? deactivateProvider(confirmAction.provider.id)
      : deleteProvider(confirmAction.provider.id);
    action
      .then(() => { setConfirmAction(null); loadProviders(); })
      .catch((e) => setError((e as Error).message))
      .finally(() => setActing(false));
  };

  if (view === "create") {
    return (
      <ProviderForm onDone={handleFormDone} onCancel={() => setView("list")} />
    );
  }

  if (typeof view === "object" && "edit" in view) {
    return (
      <ProviderForm
        provider={view.edit}
        onDone={handleFormDone}
        onCancel={() => setView("list")}
      />
    );
  }

  /* ── List view ── */
  return (
    <div>
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: "var(--space-4)",
          marginBottom: "var(--space-5)",
          flexWrap: "wrap",
        }}
      >
        <div>
          <h1 className="page-title" style={{ margin: 0 }}>
            Provider esterni
          </h1>
          <p className="page-subtitle" style={{ margin: "var(--space-1) 0 0" }}>
            Gestisci i provider che integrano TrustyCERT via API.
          </p>
        </div>
        <button className="btn btn-primary" onClick={() => setView("create")}>
          <span className="material-symbols-outlined" style={{ fontSize: 18 }}>
            add_circle
          </span>
          Nuovo provider
        </button>
      </div>

      {error && (
        <div
          className="toast toast-error"
          style={{ marginBottom: "var(--space-4)" }}
        >
          <span className="material-symbols-outlined">error</span>
          {error}
        </div>
      )}

      {loading ? (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            padding: "var(--space-8)",
          }}
        >
          <span className="spinner spinner-lg" />
        </div>
      ) : providers.length === 0 ? (
        <div
          className="card"
          style={{ textAlign: "center", padding: "var(--space-10)" }}
        >
          <span
            className="material-symbols-outlined"
            style={{
              fontSize: 56,
              color: "var(--indaco-gray-400)",
              display: "block",
              marginBottom: "var(--space-3)",
            }}
          >
            cable
          </span>
          <p
            style={{
              color: "var(--indaco-gray-600)",
              marginBottom: "var(--space-4)",
            }}
          >
            Nessun provider configurato ancora.
          </p>
          <button
            className="btn btn-primary"
            onClick={() => setView("create")}
          >
            <span
              className="material-symbols-outlined"
              style={{ fontSize: 18 }}
            >
              add_circle
            </span>
            Crea il primo provider
          </button>
        </div>
      ) : (
        <div
          style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}
        >
          {providers.map((p) => (
            <ProviderCard
              key={p.id}
              provider={p}
              onDetail={() => navigate(`/providers/${p.id}`)}
              onEdit={() => setView({ edit: p })}
              onDeactivate={() => setConfirmAction({ type: "deactivate", provider: p })}
              onDelete={() => setConfirmAction({ type: "delete", provider: p })}
            />
          ))}
        </div>
      )}

      {/* ── Confirm dialog (deactivate or delete) ── */}
      {confirmAction && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)",
          display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
          <div className="card" style={{ maxWidth: 480, width: "100%", margin: "var(--space-4)" }}>
            <div className="card-header">
              <h3 style={{ margin: 0, fontSize: "var(--font-size-md)",
                fontWeight: "var(--font-weight-semibold)",
                color: confirmAction.type === "delete" ? "var(--indaco-danger)"
                  : confirmAction.provider.active ? "var(--indaco-warning)" : "var(--indaco-success)" }}>
                {confirmAction.type === "delete"
                  ? <>Eliminare definitivamente &ldquo;{confirmAction.provider.name}&rdquo;?</>
                  : confirmAction.provider.active
                  ? <>Disattivare il provider &ldquo;{confirmAction.provider.name}&rdquo;?</>
                  : <>Riattivare il provider &ldquo;{confirmAction.provider.name}&rdquo;?</>}
              </h3>
            </div>
            <p style={{ margin: "var(--space-3) 0 var(--space-5)", fontSize: "var(--font-size-sm)",
              color: "var(--indaco-gray-700)", lineHeight: 1.5 }}>
              {confirmAction.type === "delete"
                ? <>Questa azione è <strong>irreversibile</strong>. Verranno eliminate anche tutte le
                    sessioni di firma collegate ({confirmAction.provider.session_count} sessioni).</>
                : confirmAction.provider.active
                ? <>La API key verrà <strong>bloccata immediatamente</strong>. Le sessioni e lo storico
                    vengono conservati. Potrai riattivare il provider in qualsiasi momento.</>
                : <>La API key tornerà <strong>operativa immediatamente</strong>.</>}
            </p>
            <div style={{ display: "flex", gap: "var(--space-2)", justifyContent: "flex-end" }}>
              <button className="btn btn-outline" onClick={() => setConfirmAction(null)} disabled={acting}>
                Annulla
              </button>
              <button
                className="btn btn-primary"
                style={confirmAction.type === "delete"
                  ? { background: "var(--indaco-danger)", borderColor: "var(--indaco-danger)" }
                  : confirmAction.provider.active
                  ? { background: "var(--indaco-warning)", borderColor: "var(--indaco-warning)" }
                  : undefined}
                onClick={handleConfirm}
                disabled={acting}
              >
                {acting ? <span className="spinner" /> : (
                  <span className="material-symbols-outlined" style={{ fontSize: 16 }}>
                    {confirmAction.type === "delete" ? "delete"
                      : confirmAction.provider.active ? "block" : "check_circle"}
                  </span>
                )}
                {confirmAction.type === "delete" ? "Elimina definitivamente"
                  : confirmAction.provider.active ? "Disattiva" : "Riattiva"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
